export const AUTH_RENEWAL_LOCK_NAME = 'nucleai-auth-session-renewal';
export const AUTH_RENEWAL_LEASE_KEY = 'authSessionRenewalLease';
export const AUTH_RENEWAL_LEASE_INTENT_KEY = 'authSessionRenewalLeaseIntent';
export const AUTH_RENEWAL_LEASE_TTL_MS = 10_000;
export const AUTH_RENEWAL_LEASE_POLL_MS = 25;
export const AUTH_RENEWAL_LEASE_LOST_MESSAGE = 'Auth session renewal lease ownership was lost';

export interface RenewalLease {
  ownerId: string;
  expiresAt: number;
}

export interface LockStorageLike {
  get(key: string): unknown;
  set(key: string, value: unknown): void;
  remove(key: string): void;
}

export interface RenewalLockOptions {
  ownerId: string;
  storage: LockStorageLike;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  leaseTtlMs?: number;
  pollMs?: number;
  setInterval?: (callback: () => void, ms: number) => unknown;
  clearInterval?: (intervalId: unknown) => void;
  webLocks?: Pick<LockManager, 'request'> | null;
  onLockStage?: (stage: 'requested' | 'acquired' | 'released', mechanism: 'web_lock' | 'storage_lease') => void;
}

export type RenewalLockWork<T> = (assertOwnership?: () => void) => Promise<T>;

const defaultSleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const readLease = (storage: LockStorageLike): RenewalLease | null => {
  const value = storage.get(AUTH_RENEWAL_LEASE_KEY);
  if (!value || typeof value !== 'object') {
    return null;
  }

  const lease = value as RenewalLease;
  if (typeof lease.ownerId !== 'string' || !Number.isFinite(lease.expiresAt)) {
    return null;
  }

  return lease;
};

const writeLease = (storage: LockStorageLike, ownerId: string, expiresAt: number) => {
  storage.set(AUTH_RENEWAL_LEASE_KEY, { ownerId, expiresAt });
};

const clearOwnLease = (storage: LockStorageLike, ownerId: string) => {
  const lease = readLease(storage);
  if (lease?.ownerId === ownerId) {
    storage.remove(AUTH_RENEWAL_LEASE_KEY);
  }
};

const readIntent = (storage: LockStorageLike): string | null => {
  const intent = storage.get(AUTH_RENEWAL_LEASE_INTENT_KEY);
  return typeof intent === 'string' ? intent : null;
};

const clearOwnIntent = (storage: LockStorageLike, ownerId: string) => {
  if (readIntent(storage) === ownerId) {
    storage.remove(AUTH_RENEWAL_LEASE_INTENT_KEY);
  }
};

const tryAcquireLease = async (
  storage: LockStorageLike,
  ownerId: string,
  now: number,
  leaseTtlMs: number,
  pollMs: number,
  sleep: (ms: number) => Promise<void>
): Promise<boolean> => {
  const lease = readLease(storage);
  if (lease && lease.expiresAt > now && lease.ownerId !== ownerId) {
    return false;
  }

  storage.set(AUTH_RENEWAL_LEASE_INTENT_KEY, ownerId);
  const leaseAfterIntent = readLease(storage);
  if (leaseAfterIntent && leaseAfterIntent.expiresAt > now && leaseAfterIntent.ownerId !== ownerId) {
    return false;
  }

  writeLease(storage, ownerId, now + leaseTtlMs);
  const confirmed = readLease(storage);
  if (confirmed?.ownerId !== ownerId) {
    return false;
  }
  if (readIntent(storage) === ownerId) {
    return true;
  }

  await sleep(pollMs);
  return readLease(storage)?.ownerId === ownerId;
};

export const withExpiringStorageLease = async <T>(
  work: RenewalLockWork<T>,
  options: RenewalLockOptions
): Promise<T> => {
  const now = options.now ?? Date.now;
  const sleep = options.sleep ?? defaultSleep;
  const leaseTtlMs = options.leaseTtlMs ?? AUTH_RENEWAL_LEASE_TTL_MS;
  const pollMs = options.pollMs ?? AUTH_RENEWAL_LEASE_POLL_MS;
  const startInterval = options.setInterval ?? ((callback, ms) => setInterval(callback, ms));
  const stopInterval =
    options.clearInterval ?? ((intervalId) => clearInterval(intervalId as ReturnType<typeof setInterval>));
  const { storage, ownerId } = options;

  options.onLockStage?.('requested', 'storage_lease');
  while (!(await tryAcquireLease(storage, ownerId, now(), leaseTtlMs, pollMs, sleep))) {
    await sleep(pollMs);
  }
  options.onLockStage?.('acquired', 'storage_lease');

  let ownershipLost = false;
  const assertOwnership = () => {
    if (ownershipLost || readLease(storage)?.ownerId !== ownerId) {
      throw new Error(AUTH_RENEWAL_LEASE_LOST_MESSAGE);
    }
  };
  const renewalInterval = startInterval(() => {
    if (readLease(storage)?.ownerId !== ownerId) {
      ownershipLost = true;
      return;
    }
    writeLease(storage, ownerId, now() + leaseTtlMs);
  }, Math.max(1, Math.floor(leaseTtlMs / 2)));

  try {
    const result = await work(assertOwnership);
    assertOwnership();
    return result;
  } finally {
    stopInterval(renewalInterval);
    clearOwnLease(storage, ownerId);
    clearOwnIntent(storage, ownerId);
    options.onLockStage?.('released', 'storage_lease');
  }
};

export const withRenewalLock = async <T>(work: RenewalLockWork<T>, options: RenewalLockOptions): Promise<T> => {
  const webLocks =
    options.webLocks === undefined ? (typeof navigator !== 'undefined' ? navigator.locks : null) : options.webLocks;

  if (webLocks?.request) {
    options.onLockStage?.('requested', 'web_lock');
    return webLocks.request(AUTH_RENEWAL_LOCK_NAME, async () => {
      options.onLockStage?.('acquired', 'web_lock');
      try {
        return await work(() => undefined);
      } finally {
        options.onLockStage?.('released', 'web_lock');
      }
    });
  }

  return withExpiringStorageLease(work, options);
};
